// The Locks part of the Doors screen (Admin): each deadbolt's bolt, battery and signal, Lock / Unlock with a typed
// Admin PIN, and the auto-lock switch. Wording + calls: features/security/locks.js and useDeadbolts.js. It reads apart
// from the door board and shows nothing until a lock exists, so a failed read here never disturbs the rest of the page.

import React from 'react';
import { desktopUi } from '../features/desktopUi';
import { useDeadbolts, useLockControls } from '../features/security/useDeadbolts';
import { autoLockNote, lockActivity, lockHealthLine, lockStateView } from '../features/security/locks';

const TONE = {
  ok: { bg: '#d1f2dd', fg: '#14532d', border: '#16a34a' },
  bad: { bg: '#fde2e1', fg: '#7f1d1d', border: '#dc2626' },
  warn: { bg: '#fff1cc', fg: '#713f12', border: '#d97706' },
  muted: { bg: '#e9ecef', fg: '#495057', border: '#adb5bd' },
};

const styles = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 },
  card: (tone) => ({ borderLeft: `6px solid ${TONE[tone].border}` }),
  badge: (tone) => ({ background: TONE[tone].bg, color: TONE[tone].fg, border: `1px solid ${TONE[tone].border}`,
    borderRadius: 6, padding: '2px 10px', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }),
  small: { fontSize: 13 },
  activity: (tone) => ({ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, color: TONE[tone].fg }),
};

function LockCard({ lock, data, readFailed, onChanged, onFollow, onRefused }) {
  const { busy, msg, press, toggleAuto } = useLockControls({ lock, data, ui: desktopUi, onChanged, onFollow, onRefused });
  const view = lockStateView(lock, readFailed);
  const activity = lockActivity(data, lock.lock_id);

  return (
    <div className="card" style={styles.card(view.tone)} data-testid={`lock-${lock.lock_id}`}>
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h5 className="mb-0">{lock.name}</h5>
          <span style={styles.badge(view.tone)}>{view.label}</span>
        </div>
        {view.note && <div className="mt-1" style={{ ...styles.small, color: TONE[view.tone].fg }}>{view.note}</div>}
        <div className="mt-1 text-muted" style={styles.small}>{lockHealthLine(lock)}</div>
        <div className="d-flex gap-2 mt-2">
          <button type="button" className="btn btn-sm btn-dark" disabled={busy} onClick={() => press('lock')}>Lock</button>
          <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => press('unlock')}>Unlock</button>
        </div>
        {msg && <div className={`alert alert-${msg.kind} py-1 px-2 mt-2 mb-0`} style={styles.small} role="status">{msg.text}</div>}
        <label className="form-check mt-2 mb-0" style={styles.small}>
          <input type="checkbox" className="form-check-input" checked={lock.auto_lock} disabled={busy} onChange={toggleAuto} />
          <span className="form-check-label">Lock when the shop arms</span>
        </label>
        <div className="text-muted" style={styles.small}>{autoLockNote(lock)}</div>
        {activity.length > 0 && (
          <div className="mt-2" style={{ display: 'grid', gap: 2 }}>
            {activity.map((a) => (
              <div key={a.key} style={styles.activity(a.tone)}><span>{a.text}</span><span>{a.when}</span></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function DoorLocksSection({ onRefused, paused }) {
  const { data, readFailed, load, follow } = useDeadbolts({ active: !paused, onRefused });
  const locks = (data && data.locks) || [];
  if (locks.length === 0) {
    if (!readFailed) return null;
    return <div className="alert alert-warning py-2 mb-4">Deadbolts: JobQ isn't answering, so they can't be shown right now.</div>;
  }
  return (
    <>
      <h4 className="mb-2">Deadbolts</h4>
      <div style={styles.grid} className="mb-4">
        {locks.map((l) => (
          <LockCard key={l.lock_id} lock={l} data={data} readFailed={readFailed} onChanged={load} onFollow={follow} onRefused={onRefused} />
        ))}
      </div>
    </>
  );
}
